<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import { ref, watch, computed, onMounted, onBeforeUnmount } from "vue";
import { Inertia } from "@inertiajs/inertia";
import _ from "lodash";
import SearchSelect from "@/Components/SearchSelect.vue";
import { useTenantButtonVisibility, useTenantTextRules } from "@/helpers";


const props = defineProps({
  filters: Object,
  clients: Array,
  garments: Array,
  scales: Array,
  fabrics: Array,
  boxes: Array,
  locations: Array,
  styleNumbers: Array,
});

const { resolveTextRule } = useTenantTextRules();
const { isButtonVisible } = useTenantButtonVisibility();
const garmentLabel = resolveTextRule(
  'inventory.filters.garment_label',
  resolveTextRule('garment.label', 'Garment', 'inventory.filters.garment_label'),
  'inventory.filters.garment_label'
);
const styleNumberLabel = resolveTextRule(
  'inventory.filters.style_number_label',
  'Style Number',
  'inventory.filters.style_number_label'
);
const sizeScaleLabel = resolveTextRule(
  'inventory.filters.size_scale_label',
  'Size Scale',
  'inventory.filters.size_scale_label'
);
const fabricLabel = resolveTextRule(
  'inventory.filters.fabric_label',
  'Fabric',
  'inventory.filters.fabric_label'
);
const locationLabel = resolveTextRule(
  'inventory.filters.location_label',
  'Location',
  'inventory.filters.location_label'
);
const boxLabel = resolveTextRule(
  'inventory.filters.box_label',
  'Box #',
  'inventory.filters.box_label'
);
const notesSearchLabel = resolveTextRule(
  'inventory.filters.notes_search_label',
  'Notes search',
  'inventory.filters.notes_search_label'
);
const notesSearchPlaceholder = resolveTextRule(
  'inventory.filters.notes_search_placeholder',
  'Search by notes',
  'inventory.filters.notes_search_placeholder'
);

const defaultFilters = {
  client: null,
  garmentId: null,
  scaleNameId: null,
  fabricId: null,
  styleNumber: null,
  color: "",
  box_num: "",
  sex: "",
  notes_search: "",
  status: "active",
  location: null,
};

const filterValues = ref(Object.assign({}, defaultFilters, props.filters));
const searching = ref(false);

watch(
  () => props.filters,
  (filters) => {
    filterValues.value = Object.assign({}, defaultFilters, filters || {});
  },
  { deep: true }
);

const resetFilters = () => {
  searching.value = true;
  Inertia.get(route(route().current(), {}), {}, { preserveScroll: true });
  searching.value = false;
};

const makeSearchRequest = _.debounce(() => executeSearchRequest(), 400);

const executeSearchRequest = () => {
  searching.value = true;
  const queryParameters = { ...route().params };
  delete queryParameters.page;

  queryParameters["filters"] = filterValues.value;

  Inertia.get(route(route().current(), queryParameters), {}, {
    preserveScroll: true,
    preserveState: true,
    onFinish: () => (searching.value = false),
  });
};

const styleOpen = ref(false);
const styleQuery = ref("");
const styleHighlighted = ref(-1);

const closeStyle = () => { styleOpen.value = false; styleHighlighted.value = -1; };

const onDocClick = (ev) => {
  const root = document.getElementById("styleNumber-searchable");
  if (root && !root.contains(ev.target)) closeStyle();
};
onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));

const boxOptions = computed(() =>
  (props.boxes || [])
    .filter(b => b?.box != null && b.box !== "")
    .map(b => ({ label: String(b.box), value: String(b.box) }))
);

const boxModel = computed({
  get: () => (filterValues.value.box_num ? String(filterValues.value.box_num) : null),
  set: (v) => {
    filterValues.value.box_num = v || "";
  },
});

const clientOptions = computed(() =>
  (props.clients || [])
    .filter((client) => client?.client && (client?.ID != null || client?.id != null))
    .map((client) => ({ label: client.client, value: client.client }))
);

const clientModel = computed({
  get: () => (filterValues.value.client ? String(filterValues.value.client) : null),
  set: (v) => {
    filterValues.value.client = v || null;
  },
});

const locationDisplayLabel = (location) =>
  location.identifier ? `${location.name} (${location.identifier})` : location.name;

const locationOptions = computed(() =>
  (props.locations || [])
    .filter((location) => location?.locationId != null && location?.name != null)
    .map((location) => ({
      label: locationDisplayLabel(location),
      value: locationDisplayLabel(location),
    }))
);

const locationModel = computed({
  get: () => (filterValues.value.location ? String(filterValues.value.location) : null),
  set: (v) => {
    filterValues.value.location = v || null;
  },
});

const styleOptions = computed(() =>
  (props.styleNumbers || []).map(s => {
    const val = s?.styleNumber ?? s;
    return { label: String(val), value: String(val) };
  })
);

const styleModel = computed({
  get: () => (filterValues.value.styleNumber ? String(filterValues.value.styleNumber) : null),
  set: (v) => {
    filterValues.value.styleNumber = v || null;
  },
});
</script>

<template>
  <div class="grid md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6 rounded">
    <!-- Client -->
    <div v-if="isButtonVisible('inventory.filters.client')">
      <InputLabel for="client" value="Client" />
      <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="Please select" :disabled="searching"
        @change="executeSearchRequest" />
    </div>

    <!-- Garment -->
    <div>
      <InputLabel for="garmentId" :value="garmentLabel" />
      <SelectInput id="garmentId" class="mt-1"
        v-model="filterValues.garmentId" @change="executeSearchRequest" :disabled="searching">
        <option :value="null">Please select</option>
        <option v-for="garment in garments" :key="garment.garmentID" :value="garment.garmentID">
          {{ garment.garmentName }}
        </option>
      </SelectInput>
    </div>

    <!-- Style Number -->
    <div>
      <InputLabel for="styleNumber" :value="styleNumberLabel" />
      <SearchSelect v-model="styleModel" :options="styleOptions" placeholder="Please select" :disabled="searching"
        @change="executeSearchRequest" />
    </div>

    <!-- Size Scale -->
    <div>
      <InputLabel for="scaleNameId" :value="sizeScaleLabel" />
      <SelectInput id="scaleNameId" class="mt-1"
        v-model="filterValues.scaleNameId" @change="executeSearchRequest" :disabled="searching">
        <option :value="null">Please select</option>
        <option v-for="scale in scales" :key="scale.scaleId" :value="scale.scaleId">{{ scale.scaleName }}</option>
      </SelectInput>
    </div>

    <!-- Color -->
    <div>
      <InputLabel for="color" value="Color" />
      <TextInput id="color" type="text" class="mt-1 w-full" placeholder="Search color" v-model="filterValues.color"
        @keyup="makeSearchRequest" />
    </div>

    <div>
      <InputLabel for="location" :value="locationLabel" />
      <SearchSelect v-model="locationModel" :options="locationOptions" placeholder="Please select" :disabled="searching"
        @change="executeSearchRequest" />
    </div>

    <!-- Fabric -->
    <div>
      <InputLabel for="fabricId" :value="fabricLabel" />
      <SelectInput id="fabricId" class="mt-1"
        v-model="filterValues.fabricId" @change="executeSearchRequest" :disabled="searching">
        <option :value="null">Please select</option>
        <option v-for="fabric in fabrics" :key="fabric.fabricID" :value="fabric.fabricID">{{ fabric.fabName }}</option>
      </SelectInput>
    </div>

    <!-- Box -->
    <!--<div>
      <InputLabel for="box_num" value="Box #" />
      <select id="box_num"
        class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full p-2.5"
        v-model="filterValues.box_num" @change="executeSearchRequest" :disabled="searching">
        <option value="">Please select</option>
        <option v-for="box in boxes" :key="box.box" :value="box.box">{{ box.box }}</option>
      </select>
    </div>-->

    <div v-if="isButtonVisible('inventory.filters.box_number')">
      <InputLabel for="box_num" :value="boxLabel" />
      <SearchSelect v-model="boxModel" :options="boxOptions" placeholder="Please select" :disabled="searching" :selectOnExactMatch="false"
        @change="executeSearchRequest" />
    </div>

    <!-- Sex -->
    <div v-if="isButtonVisible('inventory.filters.gender')">
      <InputLabel for="sex" value="Sex" />
      <SelectInput id="sex" class="mt-1"
        v-model="filterValues.sex" @change="executeSearchRequest" :disabled="searching">
        <option value="">Please select</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="unisex">Unisex</option>
      </SelectInput>
    </div>

    <!-- Notes search -->
    <div>
      <InputLabel for="notes_search" :value="notesSearchLabel" />
      <TextInput id="notes_search" type="text" class="mt-1 w-full" :placeholder="notesSearchPlaceholder"
        v-model="filterValues.notes_search" @keyup="makeSearchRequest" />
    </div>

    <!-- Status -->
    <div>
      <InputLabel for="status" value="Status" />
      <SelectInput id="status" class="mt-1"
        v-model="filterValues.status" @change="executeSearchRequest" :disabled="searching">
        <option value="active">Active</option>
        <option value="closed">Closed</option>
        <option value="all">All</option>
      </SelectInput>
    </div>

    <!-- Reset -->
    <div>
      <InputLabel value="Reset Filters" class="text-gray-100" />
      <SecondaryButton @click="resetFilters" class="mt-1 h-[42px]">Reset Filters</SecondaryButton>
    </div>
  </div>
</template>
